/**
 * Timezone helpers for converting between a naive (wall-clock) datetime in a
 * specific IANA timezone and a UTC ISO instant.
 *
 * Extracted from CampaignFormPage so the campaign create/edit form AND the
 * inline date editors (Queue + Scheduled Sends tabs) share one correct
 * implementation. Uses native Intl only — no tz library dependency.
 */

/**
 * Today's calendar date in an IANA timezone, as YYYY-MM-DD (SDA-268).
 *
 * `new Date().toISOString().split('T')[0]` is UTC even in a browser, so it rolls
 * over at 8 PM ET and rejects a valid same-day date for the rest of the evening.
 * Defaults to Eastern because that is the business timezone the API validates
 * against (workers/src/utils/et-dates.ts) — a client-side guard must predict the
 * server's answer, not the viewer's local date, or the two disagree for any
 * operator outside ET.
 */
export function todayInTimezone(timezone = 'America/New_York'): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: timezone });
}

/**
 * Convert a naive datetime string (from date/time inputs) + IANA timezone to a
 * UTC ISO string.
 * e.g., "2026-02-10T15:00" + "America/New_York" → "2026-02-10T20:00:00.000Z"
 */
export function naiveDatetimeToUTC(datetime: string, timezone: string): string {
  const [y, mo, d, h, mi] = datetime.split(/[-T:]/).map(Number);
  const asUTC = Date.UTC(y!, mo! - 1, d, h, mi);

  // Find the offset: format the same UTC instant in both UTC and target timezone
  const ref = new Date(asUTC);
  const utcStr = ref.toLocaleString('sv-SE', { timeZone: 'UTC' });
  const tzStr = ref.toLocaleString('sv-SE', { timeZone: timezone });
  const offsetMs =
    Date.parse(utcStr.replace(' ', 'T')) -
    Date.parse(tzStr.replace(' ', 'T'));

  return new Date(asUTC + offsetMs).toISOString();
}

/**
 * Convert a UTC ISO string to a naive datetime string for a specific timezone.
 * e.g., "2026-02-10T20:00:00.000Z" + "America/New_York" → "2026-02-10T15:00"
 */
export function utcToNaiveDatetime(utcISO: string, timezone: string): string {
  const date = new Date(utcISO);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value || '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}
